import { ExternalLink, QrCode } from 'lucide-react';
import { EDITOR_DESKTOP_MEDIA_QUERY } from '@/components/app-editor/use-editor-chrome';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useMediaQuery } from '@/hooks/use-mobile';
import { useI18nContext } from '@/i18n/i18n-react';
import {
  WebsiteBannerPreviewSidebar,
  type WebsiteBannerPreviewSidebarProps,
} from './preview-sidebar';

export type LivePreviewActionsProps = Omit<
  WebsiteBannerPreviewSidebarProps,
  'LL'
>;

/**
 * Where to see the banner for real: a QR popover on the desktop (>= 1024px),
 * "Ver no meu site" (opens the live session URL) on the phone.
 */
export function LivePreviewActions(props: LivePreviewActionsProps) {
  const { LL } = useI18nContext();
  const isDesktop = useMediaQuery(EDITOR_DESKTOP_MEDIA_QUERY);

  if (isDesktop) {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm">
            <QrCode aria-hidden className="size-4" />
            {LL.websiteBannerEditor.canvas.viewOnPhone()}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0">
          <WebsiteBannerPreviewSidebar {...props} LL={LL} />
        </PopoverContent>
      </Popover>
    );
  }

  const { bannerId, livePreviewUrl } = props;
  return (
    <div className="flex flex-col items-center gap-1 text-center">
      <Button
        variant="outline"
        className="max-md:min-h-touch"
        disabled={!livePreviewUrl}
        onClick={() =>
          window.open(livePreviewUrl, '_blank', 'noopener,noreferrer')
        }
      >
        <ExternalLink aria-hidden className="size-4" />
        {LL.websiteBannerEditor.canvas.viewOnMySite()}
      </Button>
      {!livePreviewUrl && (
        <p className="text-xs text-muted-foreground">
          {bannerId
            ? LL.websiteBannerPage.livePreview.couldNotStartSession()
            : LL.websiteBannerPage.livePreview.saveBannerFirst()}
        </p>
      )}
    </div>
  );
}
